import React, { useEffect, useState, useCallback } from 'react';
import nocApi from '../utils/nocApi';
import { defaultSystemsConfig, defaultCategoryConfig, defaultCompaniesConfig, vsatBeams } from './nocConfig';
import {
  t, panel, inputCss, labelCss, btnPrimary, btnGhost, btnDanger, iconBtn, th, td,
  Field, Row, Modal, Card, Badge, Empty, useConfirm, useToast, useFields, thresholdText,
} from './nocUi';

export const MONITORING_DEFAULTS = {
  logTypes: ['VSAT', 'Starlink'],
  statuses: ['Operational', 'Degraded', 'Down', 'Maintenance'],
  activities: ['Signal check', 'Link test', 'Reboot', 'Speed test', 'Alarm review'],
  staleHours: 6,
};

const TABS = [
  ['access', 'Access'], ['permissions', 'Permissions'], ['notifications', 'Notifications'],
  ['tickets', 'Tickets'], ['visits', 'Site Visits'], ['maintenance', 'Maintenance'],
  ['shift', 'Shift Report'], ['monitoring', 'Monitoring'],
];

export default function SettingsView({ onConfigChanged }) {
  const [tab, setTab] = useState('access');
  const [toast, toastNode] = useToast();
  const [confirm, confirmNode] = useConfirm();
  const ctx = { toast, confirm, onConfigChanged: onConfigChanged || (() => {}) };
  return (
    <div>
      <h2 style={{ fontSize: 21, fontWeight: 800, margin: '0 0 4px' }}>NOC Settings</h2>
      <div style={{ fontSize: 12.5, color: t.textMuted, marginBottom: 16 }}>Control access, notifications and every form in NOC Operations. Changes apply immediately for all users.</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${t.panelBorder}`, flexWrap: 'wrap' }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ background: 'none', border: 'none', borderBottom: `2px solid ${tab === k ? t.cyan : 'transparent'}`,
            color: tab === k ? t.cyan : t.textMuted, fontWeight: 700, fontSize: 13, padding: '10px 14px', cursor: 'pointer', marginBottom: -1 }}>{l}</button>
        ))}
      </div>
      {tab === 'access' && <AccessTab ctx={ctx} />}
      {tab === 'permissions' && <PermissionsTab ctx={ctx} />}
      {tab === 'notifications' && <NotificationsTab ctx={ctx} />}
      {tab === 'tickets' && <>
        <NumberingCard ctx={ctx} />
        <ListConfigCard ctx={ctx} title="Systems" configKey="sct-systems-config" defaults={defaultSystemsConfig()} help="The systems a ticket, visit or request can belong to." />
        <CategoryCard ctx={ctx} />
        <CompaniesCard ctx={ctx} />
        <FieldsManager ctx={ctx} section="tickets" title="Additional ticket fields"
          intro="Add your own fields to the ticket form, for example an Action dropdown (Reboot, Replace, Escalate…). Every user sees them; only admins change them here." />
      </>}
      {tab === 'visits' && <FieldsManager ctx={ctx} section="site_visits" title="Additional site visit fields"
        intro="Built-in fields (Site name, Location, Visitor, Contact, Date, Company, Equipment, System, Technician, Purpose, Notes) are always shown. Add any extra fields here." />}
      {tab === 'maintenance' && <>
        <TeamsCard ctx={ctx} />
        <FieldsManager ctx={ctx} section="maintenance" title="Additional maintenance fields"
          intro="Extra fields on every maintenance request, whether it is created directly or forwarded from a ticket or site visit." />
      </>}
      {tab === 'shift' && <FieldsManager ctx={ctx} section="shift_reports" withThreshold withGroups title="Shift report parameters"
        intro="These are all the parameters on the shift performance report, including the original ones. Rename, reorder, group, delete, or add parameters, and set an optimal range. When an operator enters a value outside the range, the field is flagged immediately and the alarm is included in the notification email." />}
      {tab === 'monitoring' && <>
        <MonitoringOptionsCard ctx={ctx} />
        <FieldsManager ctx={ctx} section="monitoring" withThreshold title="Additional log check fields"
          intro="Extra fields operators fill when they log a monitoring check (for example Signal level dB or Ping ms). Dropdown fields are charted on the Monitoring dashboard; numeric fields can have an optimal range." />
      </>}
      {toastNode}{confirmNode}
    </div>
  );
}

// ═════════════════ ACCESS ═════════════════
function AccessTab({ ctx }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState('');
  const load = useCallback(() => { nocApi.get('/access').then(setRows).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  const setRole = async (u, role) => {
    setBusy(u.id);
    try { await nocApi.put(`/access/${u.id}`, { role }); await load(); ctx.toast(`${u.full_name}: access updated`); }
    catch (e) { ctx.toast(e?.error || 'Failed', 'err'); } finally { setBusy(null); }
  };
  if (rows === null) return <div style={{ color: t.textMuted }}>Loading…</div>;
  const list = rows.filter(u => !q || `${u.full_name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Card title="Who can use NOC Operations" right={<input placeholder="Search people…" value={q} onChange={e => setQ(e.target.value)} style={{ ...inputCss, width: 220 }} />}>
      <div style={{ fontSize: 12.5, color: t.textMuted, marginBottom: 12 }}><b>Admin</b> has full control: add, edit and delete everything, and configure all settings. <b>User</b> works within the permissions you set on the Permissions tab. <b>No access</b> removes NOC entirely.</div>
      <div style={{ overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ borderBottom: `1px solid ${t.panelBorder}` }}>{['User', 'Email', 'Access level'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {list.map(u => {
              const level = u.is_super ? 'admin' : (u.techops_role === 'admin' ? 'admin' : (u.noc_enabled && u.techops_role !== 'none' ? 'user' : 'none'));
              return (
                <tr key={u.id} style={{ borderBottom: `1px solid ${t.panelBorder}` }}>
                  <td style={{ ...td, fontWeight: 600 }}>{u.full_name} {u.is_super && <Badge color={t.cyan}>Super admin</Badge>}</td>
                  <td style={{ ...td, color: t.textMuted }}>{u.email || '—'}</td>
                  <td style={td}>
                    {u.is_super ? <Badge color={t.green}>Admin (always)</Badge>
                      : <select disabled={busy === u.id} value={level} onChange={e => setRole(u, e.target.value)} style={{ ...inputCss, width: 160 }}>
                          <option value="none">No access</option><option value="user">User</option><option value="admin">Admin</option>
                        </select>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ═════════════════ PERMISSIONS ═════════════════
const SECTION_LABELS = { tickets: 'Tickets', site_visits: 'Site Visits', maintenance: 'Maintenance', shift_reports: 'Shift Reports', monitoring: 'Monitoring' };
const ACTIONS = [['view', 'View'], ['add', 'Add'], ['edit', 'Edit'], ['delete', 'Delete']];
function PermissionsTab({ ctx }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    nocApi.get('/permissions').then(d => { setData(d); setSel(s => s || (d.users.find(u => !u.is_admin) || d.users[0] || {}).id); })
      .catch(() => setData({ sections: [], users: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (data === null) return <div style={{ color: t.textMuted }}>Loading…</div>;
  const user = data.users.find(u => u.id === sel);
  const sections = (data.sections && data.sections.length ? data.sections : Object.keys(SECTION_LABELS));
  const write = async (section, next) => {
    setSaving(true);
    try { await nocApi.put(`/permissions/${user.id}`, { section, can_view: next.view, can_add: next.add, can_edit: next.edit, can_delete: next.delete }); await load(); }
    catch (e) { ctx.toast(e?.error || 'Failed', 'err'); } finally { setSaving(false); }
  };
  const setBit = (section, action, val) => {
    const cur = { view: false, add: false, edit: false, delete: false, ...((user.permissions || {})[section] || {}) };
    const next = { ...cur, [action]: val };
    if (val && action !== 'view') next.view = true;             // any right implies view
    if (!val && action === 'view') { next.add = next.edit = next.delete = false; }
    write(section, next);
  };
  const preset = (section, kind) => {
    const map = { none: [0, 0, 0, 0], view: [1, 0, 0, 0], fill: [1, 1, 0, 0], full: [1, 1, 1, 1] }[kind];
    write(section, { view: !!map[0], add: !!map[1], edit: !!map[2], delete: !!map[3] });
  };
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ ...panel, width: 260, flex: '0 0 auto', maxHeight: 520, overflow: 'auto' }}>
        {data.users.map(u => (
          <button key={u.id} onClick={() => setSel(u.id)}
            style={{ width: '100%', textAlign: 'left', background: sel === u.id ? t.cyan : 'transparent', color: sel === u.id ? '#fff' : t.textPrimary,
              border: 'none', borderBottom: `1px solid ${t.panelBorder}`, padding: '10px 14px', cursor: 'pointer', fontSize: 13 }}>
            <div style={{ fontWeight: 600 }}>{u.full_name}</div>
            <div style={{ fontSize: 11, opacity: 0.75 }}>{u.is_admin ? 'Admin — full access' : (u.email || 'User')}</div>
          </button>
        ))}
      </div>
      <Card style={{ flex: 1, minWidth: 380 }} title={user ? user.full_name : 'Select a user'}>
        {!user ? null : user.is_admin ? (
          <div style={{ color: t.textMuted, fontSize: 13 }}>This person is an admin and always has full access to every page. Change them to User on the Access tab to limit what they can do.</div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: t.textMuted, marginBottom: 14 }}>Tick what this user may do on each page, or use a quick preset. Rules are enforced on the server, not just hidden in the screen.</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ borderBottom: `1px solid ${t.panelBorder}` }}>
                <th style={th}>Page</th>{ACTIONS.map(([a, l]) => <th key={a} style={{ ...th, textAlign: 'center' }}>{l}</th>)}<th style={th}>Quick preset</th>
              </tr></thead>
              <tbody>
                {sections.map(section => {
                  const p = (user.permissions || {})[section] || {};
                  return (
                    <tr key={section} style={{ borderBottom: `1px solid ${t.panelBorder}` }}>
                      <td style={{ ...td, fontWeight: 600 }}>{SECTION_LABELS[section] || section}</td>
                      {ACTIONS.map(([a]) => (
                        <td key={a} style={{ ...td, textAlign: 'center' }}>
                          <input type="checkbox" disabled={saving} checked={!!p[a]} onChange={e => setBit(section, a, e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                        </td>
                      ))}
                      <td style={td}>
                        <select value="" disabled={saving} onChange={e => e.target.value && preset(section, e.target.value)} style={{ ...inputCss, padding: '5px 8px', width: 150 }}>
                          <option value="">Apply…</option><option value="none">No access</option><option value="view">View only</option>
                          <option value="fill">Fill in data</option><option value="full">Full control</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: 11.5, color: t.textMuted, marginTop: 10 }}>"Fill in data" = view and add new entries, without editing or deleting existing ones.</div>
          </>
        )}
      </Card>
    </div>
  );
}

// ═════════════════ NOTIFICATIONS ═════════════════
function NotificationsTab({ ctx }) {
  const [cfg, setCfg] = useState(null);
  const [toText, setToText] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const load = useCallback(() => {
    nocApi.get('/notify-config').then(c => { setCfg(c); setToText((c.to || []).join(', ')); }).catch(e => setCfg({ error: e?.error || 'Failed to load' }));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!cfg) return <div style={{ color: t.textMuted }}>Loading…</div>;
  if (cfg.error) return <div style={{ color: t.red }}>{cfg.error}</div>;
  const save = async () => {
    setSaving(true);
    try { await nocApi.put('/notify-config', { enabled: cfg.enabled, from: cfg.from, to: toText, notifyCreator: cfg.notifyCreator }); ctx.toast('Notification settings saved'); load(); }
    catch (e) { ctx.toast(e?.error || 'Failed', 'err'); } finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try { await save(); const r = await nocApi.post('/notify-config/test', {}); ctx.toast(`Test email sent to ${r.to.join(', ')}`); }
    catch (e) { ctx.toast(e?.error || 'Test failed', 'err'); } finally { setTesting(false); }
  };
  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 760 }}>
      {!cfg.smtpConfigured && <div style={{ ...panel, padding: 14, borderColor: t.amber, color: t.amber, fontSize: 13 }}>⚠ The platform email (SMTP) is not configured yet, so nothing can be sent. Set it up once in the platform mail settings; NOC will use it automatically.</div>}
      <Card title="Email notifications">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, fontWeight: 600, marginBottom: 16 }}>
          <input type="checkbox" checked={cfg.enabled} onChange={e => setCfg({ ...cfg, enabled: e.target.checked })} style={{ width: 16, height: 16 }} />
          Send email notifications for NOC activity
        </label>
        <Field label="Notification recipients" hint="One or more addresses, separated by commas. They receive every NOC event: tickets, site visits, maintenance requests and shift reports.">
          <textarea rows={3} style={{ ...inputCss, resize: 'vertical' }} value={toText} onChange={e => setToText(e.target.value)} placeholder="noc-team@omansat.com, supervisor@omansat.com" />
        </Field>
        <Field label="Sender address (From)" hint={`Leave empty to use the platform's default mailbox (${cfg.defaultFrom || 'not configured'}). If you set a different address, it must be one the mail server is allowed to send as.`}>
          <input style={inputCss} value={cfg.from} onChange={e => setCfg({ ...cfg, from: e.target.value })} placeholder={cfg.defaultFrom || 'default mailbox'} />
        </Field>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, marginBottom: 6 }}>
          <input type="checkbox" checked={cfg.notifyCreator} onChange={e => setCfg({ ...cfg, notifyCreator: e.target.checked })} style={{ width: 16, height: 16, marginTop: 2 }} />
          <span>Also email the person who created the item<div style={{ fontSize: 11.5, color: t.textMuted, marginTop: 2 }}>When an internal user creates a ticket, visit or request, they are notified of every change to it (if their account has an email).</div></span>
        </label>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
          <button style={btnGhost} onClick={test} disabled={testing || saving}>{testing ? 'Sending…' : 'Save and send test email'}</button>
        </div>
      </Card>
      <Card title="What triggers an email">
        <div style={{ fontSize: 13, color: t.textPrimary, lineHeight: 1.8 }}>
          • Ticket created, edited, status changed or deleted<br />
          • Site visit created, edited, status changed or deleted<br />
          • Maintenance request raised, forwarded to a team, updated or cancelled (the team's emails are included)<br />
          • Shift performance report submitted, with any out-of-range parameters highlighted
        </div>
      </Card>
    </div>
  );
}

// ═════════════════ TICKET NUMBERING ═════════════════
function NumberingCard({ ctx }) {
  const [cfg, setCfg] = useState({ prefix: 'SCT', start: 1 });
  useEffect(() => { nocApi.get('/config/ticket-id-config').then(c => { if (c) setCfg({ prefix: c.prefix || 'SCT', start: c.start || 1 }); }).catch(() => {}); }, []);
  const save = async () => {
    try { await nocApi.put('/config/ticket-id-config', { value: { prefix: (cfg.prefix || 'SCT').trim(), start: parseInt(cfg.start, 10) || 1 } }); ctx.toast('Ticket numbering saved'); ctx.onConfigChanged(); }
    catch (e) { ctx.toast(e?.error || 'Failed', 'err'); }
  };
  return (
    <Card title="Ticket numbering" style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12.5, color: t.textMuted, marginBottom: 12 }}>New ticket IDs look like <b>{cfg.prefix || 'SCT'}-VSAT_{String(Math.max(1, parseInt(cfg.start, 10) || 1)).padStart(2, '0')}</b>. Existing IDs are not changed.</div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ width: 200 }}><label style={labelCss}>Prefix</label><input style={inputCss} value={cfg.prefix} onChange={e => setCfg({ ...cfg, prefix: e.target.value })} /></div>
        <div style={{ width: 140 }}><label style={labelCss}>Start number</label><input type="number" min="1" style={inputCss} value={cfg.start} onChange={e => setCfg({ ...cfg, start: e.target.value })} /></div>
        <button style={btnPrimary} onClick={save}>Save</button>
      </div>
    </Card>
  );
}

// ═════════════════ GENERIC OPTION LIST EDITOR ═════════════════
// Edits an ordered list of strings: rename inline, reorder, remove, add.
export function OptionListEditor({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const list = value || [];
  const add = () => { const v = draft.trim(); if (!v || list.includes(v)) return; onChange([...list, v]); setDraft(''); };
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= list.length) return; const n = [...list]; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  return (
    <div>
      {list.length === 0 && <div style={{ fontSize: 12.5, color: t.textMuted, marginBottom: 8 }}>No options yet.</div>}
      {list.map((o, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input style={{ ...inputCss, padding: '7px 10px' }} value={o} onChange={e => { const n = [...list]; n[i] = e.target.value; onChange(n); }} />
          <button type="button" style={iconBtn} onClick={() => move(i, -1)} title="Move up">↑</button>
          <button type="button" style={iconBtn} onClick={() => move(i, 1)} title="Move down">↓</button>
          <button type="button" style={{ ...iconBtn, color: t.red }} onClick={() => onChange(list.filter((_, k) => k !== i))} title="Remove">✕</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <input style={{ ...inputCss, padding: '7px 10px' }} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder={placeholder || 'Add an option and press Enter'} />
        <button type="button" style={btnGhost} onClick={add}>+ Add</button>
      </div>
    </div>
  );
}

function ListConfigCard({ ctx, title, configKey, defaults, help }) {
  const [list, setList] = useState(null);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { nocApi.get(`/config/${configKey}`).then(v => setList(Array.isArray(v) ? v : defaults)).catch(() => setList(defaults)); }, [configKey]); // eslint-disable-line
  if (!list) return null;
  const save = async () => {
    const clean = list.map(s => String(s).trim()).filter(Boolean);
    try { await nocApi.put(`/config/${configKey}`, { value: clean }); setList(clean); setDirty(false); ctx.toast(`${title} saved`); ctx.onConfigChanged(); }
    catch (e) { ctx.toast(e?.error || 'Failed', 'err'); }
  };
  return (
    <Card title={title} style={{ marginBottom: 16 }} right={dirty && <button style={btnPrimary} onClick={save}>Save changes</button>}>
      {help && <div style={{ fontSize: 12.5, color: t.textMuted, marginBottom: 12 }}>{help}</div>}
      <div style={{ maxWidth: 520 }}><OptionListEditor value={list} onChange={v => { setList(v); setDirty(true); }} /></div>
    </Card>
  );
}

// Categories and sub-categories per system.
function CategoryCard({ ctx }) {
  const [cfg, setCfg] = useState(null);
  const [systems, setSystems] = useState(defaultSystemsConfig());
  const [sys, setSys] = useState('');
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    nocApi.get('/config/sct-category-config').then(v => setCfg(v || defaultCategoryConfig())).catch(() => setCfg(defaultCategoryConfig()));
    nocApi.get('/config/sct-systems-config').then(v => { if (Array.isArray(v) && v.length) setSystems(v); }).catch(() => {});
  }, []);
  useEffect(() => { if (!sys && systems.length) setSys(systems[0]); }, [systems, sys]);
  if (!cfg || !sys) return null;
  const cats = cfg[sys] || [];
  const setCats = (n) => { setCfg({ ...cfg, [sys]: n }); setDirty(true); };
  const save = async () => {
    try { await nocApi.put('/config/sct-category-config', { value: cfg }); setDirty(false); ctx.toast('Categories saved'); ctx.onConfigChanged(); }
    catch (e) { ctx.toast(e?.error || 'Failed', 'err'); }
  };
  return (
    <Card title="Categories and sub-categories" style={{ marginBottom: 16 }} right={dirty && <button style={btnPrimary} onClick={save}>Save changes</button>}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: t.textMuted }}>System:</span>
        {systems.map(s => <button key={s} onClick={() => setSys(s)} style={{ ...(s === sys ? btnPrimary : btnGhost), padding: '6px 12px' }}>{s}</button>)}
      </div>
      {cats.map((c, i) => (
        <div key={i} style={{ ...panel, padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input style={{ ...inputCss, fontWeight: 700 }} value={c.name} onChange={e => { const n = [...cats]; n[i] = { ...c, name: e.target.value }; setCats(n); }} />
            <button style={btnDanger} onClick={async () => { if (await ctx.confirm({ title: 'Remove category', message: `Remove "${c.name}" and its sub-categories from ${sys}? Existing tickets keep their values.`, danger: true, confirmLabel: 'Remove' })) setCats(cats.filter((_, k) => k !== i)); }}>Remove</button>
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Sub-categories</div>
          <OptionListEditor value={c.subs || []} onChange={v => { const n = [...cats]; n[i] = { ...c, subs: v }; setCats(n); }} placeholder="Add a sub-category" />
        </div>
      ))}
      <button style={btnGhost} onClick={() => setCats([...cats, { name: 'New category', subs: [] }])}>+ Add category to {sys}</button>
    </Card>
  );
}

// Companies / sites per system (VSAT is per beam).
function CompaniesCard({ ctx }) {
  const [cfg, setCfg] = useState(null);
  const [systems, setSystems] = useState(defaultSystemsConfig());
  const [sys, setSys] = useState('');
  const [beam, setBeam] = useState('');
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    nocApi.get('/config/sct-companies-config').then(v => setCfg(v || defaultCompaniesConfig())).catch(() => setCfg(defaultCompaniesConfig()));
    nocApi.get('/config/sct-systems-config').then(v => { if (Array.isArray(v) && v.length) setSystems(v); }).catch(() => {});
  }, []);
  useEffect(() => { if (!sys && systems.length) setSys(systems[0]); }, [systems, sys]);
  if (!cfg || !sys) return null;
  const isObj = cfg[sys] && !Array.isArray(cfg[sys]) && typeof cfg[sys] === 'object';
  const beams = isObj ? Object.keys(cfg[sys]) : (sys === 'VSAT' ? vsatBeams() : []);
  const curBeam = beam && beams.includes(beam) ? beam : beams[0];
  const list = isObj ? (cfg[sys][curBeam] || []) : (Array.isArray(cfg[sys]) ? cfg[sys] : []);
  const setList = (v) => {
    if (isObj || sys === 'VSAT') setCfg({ ...cfg, [sys]: { ...(isObj ? cfg[sys] : {}), [curBeam]: v } });
    else setCfg({ ...cfg, [sys]: v });
    setDirty(true);
  };
  const save = async () => {
    try { await nocApi.put('/config/sct-companies-config', { value: cfg }); setDirty(false); ctx.toast('Companies saved'); ctx.onConfigChanged(); }
    catch (e) { ctx.toast(e?.error || 'Failed', 'err'); }
  };
  return (
    <Card title="Companies / sites" style={{ marginBottom: 16 }} right={dirty && <button style={btnPrimary} onClick={save}>Save changes</button>}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: t.textMuted }}>System:</span>
        {systems.map(s => <button key={s} onClick={() => { setSys(s); setBeam(''); }} style={{ ...(s === sys ? btnPrimary : btnGhost), padding: '6px 12px' }}>{s}</button>)}
      </div>
      {beams.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: t.textMuted }}>Beam:</span>
          {beams.map(b => <button key={b} onClick={() => setBeam(b)} style={{ ...(b === curBeam ? btnPrimary : btnGhost), padding: '5px 11px' }}>{b}</button>)}
        </div>
      )}
      <div style={{ maxWidth: 520 }}><OptionListEditor value={list} onChange={setList} placeholder="Add a company or site" /></div>
    </Card>
  );
}

// ═════════════════ MAINTENANCE TEAMS ═════════════════
function TeamsCard({ ctx }) {
  const [teams, setTeams] = useState(null);
  const [edit, setEdit] = useState(null); // {id?, name, emails}
  const load = useCallback(() => { nocApi.get('/teams').then(setTeams).catch(() => setTeams([])); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      if (edit.id) await nocApi.put(`/teams/${edit.id}`, { name: edit.name, emails: edit.emails });
      else await nocApi.post('/teams', { name: edit.name, emails: edit.emails });
      setEdit(null); load(); ctx.toast('Team saved');
    } catch (e) { ctx.toast(e?.error || 'Failed', 'err'); }
  };
  const del = async (tm) => {
    if (!(await ctx.confirm({ title: 'Delete team', message: `Delete the team "${tm.name}"? Existing requests keep their recipients.`, danger: true, confirmLabel: 'Delete' }))) return;
    try { await nocApi.delete(`/teams/${tm.id}`); load(); ctx.toast('Team deleted'); } catch (e) { ctx.toast(e?.error || 'Failed', 'err'); }
  };
  return (
    <Card title="Maintenance teams" style={{ marginBottom: 16 }} right={<button style={btnPrimary} onClick={() => setEdit({ name: '', emails: '' })}>+ Add team</button>}>
      <div style={{ fontSize: 12.5, color: t.textMuted, marginBottom: 12 }}>When a ticket, site visit or request is forwarded to maintenance, choose a team and everyone in it is emailed.</div>
      {teams === null ? <div style={{ color: t.textMuted }}>Loading…</div> : teams.length === 0 ? <Empty>No teams yet. Add your first maintenance team.</Empty> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ borderBottom: `1px solid ${t.panelBorder}` }}>{['Team', 'Members (emails)', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{teams.map(tm => (
            <tr key={tm.id} style={{ borderBottom: `1px solid ${t.panelBorder}` }}>
              <td style={{ ...td, fontWeight: 700 }}>{tm.name}</td>
              <td style={td}><div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{tm.emails.map(e => <Badge key={e} color={t.cyan}>{e}</Badge>)}</div></td>
              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                <button style={{ ...iconBtn, marginRight: 6 }} onClick={() => setEdit({ id: tm.id, name: tm.name, emails: tm.emails.join(', ') })}>Edit</button>
                <button style={{ ...iconBtn, color: t.red }} onClick={() => del(tm)}>Delete</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {edit && (
        <Modal title={edit.id ? 'Edit team' : 'Add maintenance team'} onClose={() => setEdit(null)} width={480}
          footer={<><button style={btnGhost} onClick={() => setEdit(null)}>Cancel</button><button style={btnPrimary} onClick={save}>Save team</button></>}>
          <Field label="Team name" required><input style={inputCss} value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} placeholder="e.g. RF Field Team" /></Field>
          <Field label="Emails" required hint="Separate multiple emails with commas."><textarea rows={3} style={{ ...inputCss, resize: 'vertical' }} value={edit.emails} onChange={e => setEdit({ ...edit, emails: e.target.value })} placeholder="tech1@omansat.com, tech2@omansat.com" /></Field>
        </Modal>
      )}
    </Card>
  );
}

// ═════════════════ MONITORING OPTIONS ═════════════════
function MonitoringOptionsCard({ ctx }) {
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { nocApi.get('/config/monitoring-options').then(v => setCfg({ ...MONITORING_DEFAULTS, ...(v || {}) })).catch(() => setCfg(MONITORING_DEFAULTS)); }, []);
  if (!cfg) return null;
  const upd = (k, v) => { setCfg({ ...cfg, [k]: v }); setDirty(true); };
  const save = async () => {
    const clean = { ...cfg, logTypes: cfg.logTypes.filter(Boolean), statuses: cfg.statuses.filter(Boolean), activities: cfg.activities.filter(Boolean), staleHours: Math.max(1, parseInt(cfg.staleHours, 10) || 6) };
    if (!clean.logTypes.length || !clean.statuses.length) { ctx.toast('Keep at least one log type and one status.', 'err'); return; }
    try { await nocApi.put('/config/monitoring-options', { value: clean }); setDirty(false); ctx.toast('Monitoring setup saved'); ctx.onConfigChanged(); }
    catch (e) { ctx.toast(e?.error || 'Failed', 'err'); }
  };
  return (
    <Card title="Log check setup" style={{ marginBottom: 16 }} right={dirty && <button style={btnPrimary} onClick={save}>Save changes</button>}>
      <div style={{ fontSize: 12.5, color: t.textMuted, marginBottom: 14 }}>Define what operators log in the "Log Check" window. Everything here appears in the form and on the Monitoring dashboard.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
        <div><div style={labelCss}>Log types (tabs)</div><OptionListEditor value={cfg.logTypes} onChange={v => upd('logTypes', v)} placeholder="e.g. Fibre" /></div>
        <div><div style={labelCss}>Connection statuses</div><OptionListEditor value={cfg.statuses} onChange={v => upd('statuses', v)} placeholder="e.g. Intermittent" />
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>Statuses containing "operational", "ok", "up", "normal" or "good" count as healthy.</div></div>
        <div><div style={labelCss}>Activities / checks performed</div><OptionListEditor value={cfg.activities} onChange={v => upd('activities', v)} placeholder="e.g. Firmware check" /></div>
      </div>
      <div style={{ width: 220, marginTop: 14 }}>
        <label style={labelCss}>Mark a site stale after (hours)</label>
        <input type="number" min="1" style={inputCss} value={cfg.staleHours} onChange={e => upd('staleHours', e.target.value)} />
      </div>
    </Card>
  );
}

// ═════════════════ FIELDS MANAGER ═════════════════
const TYPE_LABEL = { text: 'Text', number: 'Number', select: 'Dropdown', date: 'Date', textarea: 'Long text', checkbox: 'Yes / No' };

export function FieldsManager({ ctx, section, title, intro, withThreshold, withGroups }) {
  const [fields, reload, loaded] = useFields(section);
  const [edit, setEdit] = useState(null);
  const groups = [...new Set(fields.map(f => f.group_name).filter(Boolean))];
  const move = async (i, d) => {
    const j = i + d; if (j < 0 || j >= fields.length) return;
    const a = fields[i], b = fields[j];
    try {
      await nocApi.put(`/fields/${section}/${a.id}`, { position: b.position === a.position ? b.position + d : b.position });
      await nocApi.put(`/fields/${section}/${b.id}`, { position: a.position });
      reload();
    } catch (e) { ctx.toast(e?.error || 'Failed', 'err'); }
  };
  const del = async (f) => {
    if (!(await ctx.confirm({ title: 'Delete field', danger: true, confirmLabel: 'Delete field',
      message: `Delete "${f.label}"? It will disappear from the form for everyone. Values already saved on existing records are kept.` }))) return;
    try { await nocApi.delete(`/fields/${section}/${f.id}`); reload(); ctx.toast('Field deleted'); } catch (e) { ctx.toast(e?.error || 'Failed', 'err'); }
  };
  return (
    <Card title={title} right={<button style={btnPrimary} onClick={() => setEdit({ label: '', field_type: 'text', options: [], required: false, help: '', group_name: '', threshold: null })}>+ Add field</button>}>
      {intro && <div style={{ fontSize: 12.5, color: t.textMuted, marginBottom: 14, lineHeight: 1.55 }}>{intro}</div>}
      {!loaded ? <div style={{ color: t.textMuted }}>Loading…</div> : fields.length === 0 ? <Empty>No fields yet. Click "+ Add field" to create the first one.</Empty> : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: `1px solid ${t.panelBorder}` }}>
              <th style={{ ...th, width: 70 }}>Order</th><th style={th}>Field</th>{withGroups && <th style={th}>Group</th>}<th style={th}>Type</th>
              <th style={th}>Options</th>{withThreshold && <th style={th}>Optimal value</th>}<th style={th}>Required</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {fields.map((f, i) => (
                <tr key={f.id} style={{ borderBottom: `1px solid ${t.panelBorder}` }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button style={{ ...iconBtn, marginRight: 4 }} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                    <button style={iconBtn} disabled={i === fields.length - 1} onClick={() => move(i, 1)}>↓</button>
                  </td>
                  <td style={td}><div style={{ fontWeight: 700 }}>{f.label}</div>{f.help && <div style={{ fontSize: 11, color: t.textMuted }}>{f.help}</div>}{f.builtin ? <Badge>Original</Badge> : null}</td>
                  {withGroups && <td style={{ ...td, color: t.textMuted }}>{f.group_name || '—'}</td>}
                  <td style={td}><Badge color={t.cyan}>{TYPE_LABEL[f.field_type] || f.field_type}</Badge></td>
                  <td style={{ ...td, color: t.textMuted, maxWidth: 240 }}>{f.field_type === 'select' ? (f.options || []).join(', ') || '—' : '—'}</td>
                  {withThreshold && <td style={{ ...td, color: f.threshold ? t.green : t.textMuted }}>{thresholdText(f.threshold) || '—'}</td>}
                  <td style={td}>{f.required ? <Badge color={t.amber}>Required</Badge> : <span style={{ color: t.textMuted }}>Optional</span>}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button style={{ ...iconBtn, marginRight: 6 }} onClick={() => setEdit({ ...f, options: f.options || [], help: f.help || '', group_name: f.group_name || '' })}>Edit</button>
                    <button style={{ ...iconBtn, color: t.red }} onClick={() => del(f)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <FieldEditor ctx={ctx} section={section} field={edit} groups={groups} withThreshold={withThreshold} withGroups={withGroups}
        onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </Card>
  );
}

function FieldEditor({ ctx, section, field, groups, withThreshold, withGroups, onClose, onSaved }) {
  const [f, setF] = useState(field);
  const [th0] = useState(field.threshold || null);
  const [thMode, setThMode] = useState(th0 ? th0.mode : '');
  const [th, setTh] = useState({ min: th0 && th0.min != null ? th0.min : '', max: th0 && th0.max != null ? th0.max : '', op: th0 ? th0.op || '>' : '>', value: th0 && th0.value != null ? th0.value : '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const numeric = ['number', 'text'].includes(f.field_type);
  const save = async () => {
    if (!String(f.label || '').trim()) { ctx.toast('Give the field a name.', 'err'); return; }
    if (f.field_type === 'select' && !(f.options || []).filter(Boolean).length) { ctx.toast('Add at least one dropdown option.', 'err'); return; }
    const threshold = withThreshold && numeric && thMode ? (thMode === 'range' ? { mode: 'range', min: th.min, max: th.max } : { mode: 'compare', op: th.op, value: th.value }) : null;
    const body = { label: f.label.trim(), field_type: f.field_type, options: (f.options || []).map(s => String(s).trim()).filter(Boolean), required: !!f.required,
      help: f.help || '', group_name: withGroups ? (f.group_name || '') : undefined, threshold };
    setSaving(true);
    try {
      if (f.id) await nocApi.put(`/fields/${section}/${f.id}`, body); else await nocApi.post(`/fields/${section}`, body);
      ctx.toast(f.id ? 'Field updated' : 'Field added'); onSaved();
    } catch (e) { ctx.toast(e?.error || 'Failed', 'err'); setSaving(false); }
  };
  return (
    <Modal title={f.id ? `Edit field — ${field.label}` : 'Add a field'} onClose={onClose} width={600}
      footer={<><button style={btnGhost} onClick={onClose}>Cancel</button><button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save field'}</button></>}>
      <Row>
        <Field label="Field name" required><input style={inputCss} value={f.label} onChange={e => set('label', e.target.value)} placeholder="e.g. Action taken" autoFocus /></Field>
        <Field label="Type">
          <select style={inputCss} value={f.field_type} onChange={e => set('field_type', e.target.value)}>
            {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
      </Row>
      {withGroups && (
        <Field label="Group (section heading on the form)" hint="Pick an existing group or type a new one.">
          <input style={inputCss} list={`grp-${section}`} value={f.group_name} onChange={e => set('group_name', e.target.value)} placeholder="e.g. Temperatures" />
          <datalist id={`grp-${section}`}>{groups.map(g => <option key={g} value={g} />)}</datalist>
        </Field>
      )}
      {f.field_type === 'select' && (
        <Field label="Dropdown options" required><OptionListEditor value={f.options} onChange={v => set('options', v)} /></Field>
      )}
      <Field label="Help text (optional)" hint="Shown under the field to guide the person filling it."><input style={inputCss} value={f.help} onChange={e => set('help', e.target.value)} /></Field>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14 }}>
        <input type="checkbox" checked={!!f.required} onChange={e => set('required', e.target.checked)} style={{ width: 16, height: 16 }} /> This field is required
      </label>
      {withThreshold && numeric && (
        <div style={{ ...panel, padding: 14, background: t.soft }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, marginBottom: 4 }}>Optimal value / alarm</div>
          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 10 }}>If the value entered is outside this, the operator sees an immediate warning and it is highlighted in the report and email.</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            {[['', 'No check'], ['range', 'Optimal range'], ['compare', 'Alarm value']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setThMode(k)} style={{ ...(thMode === k ? btnPrimary : btnGhost), padding: '6px 12px' }}>{l}</button>
            ))}
          </div>
          {thMode === 'range' && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <div style={{ width: 130 }}><label style={labelCss}>Minimum</label><input type="number" step="any" style={inputCss} value={th.min} onChange={e => setTh({ ...th, min: e.target.value })} /></div>
              <div style={{ paddingBottom: 10, color: t.textMuted }}>to</div>
              <div style={{ width: 130 }}><label style={labelCss}>Maximum</label><input type="number" step="any" style={inputCss} value={th.max} onChange={e => setTh({ ...th, max: e.target.value })} /></div>
              <div style={{ fontSize: 11.5, color: t.textMuted, paddingBottom: 10 }}>Leave one empty for a one-sided limit.</div>
            </div>
          )}
          {thMode === 'compare' && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <div style={{ fontSize: 13, paddingBottom: 10 }}>Alarm when value is</div>
              <div style={{ width: 90 }}><label style={labelCss}>Condition</label>
                <select style={inputCss} value={th.op} onChange={e => setTh({ ...th, op: e.target.value })}>{['>', '>=', '<', '<=', '=', '!='].map(o => <option key={o}>{o}</option>)}</select></div>
              <div style={{ width: 130 }}><label style={labelCss}>Value</label><input type="number" step="any" style={inputCss} value={th.value} onChange={e => setTh({ ...th, value: e.target.value })} /></div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
